/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import { differenceInDays } from 'date-fns/differenceInDays';
import { parseISO } from 'date-fns/parseISO';
import { v4 as uuidv4 } from 'uuid';

import {
    PriceModifier,
    TransactionSummary,
} from '@toucan/client/global/transaction';

import {
    AccessoryPricingSelection,
    AccessorySelection,
    GetSellItemPriceParams,
    ResolveAccessoryPricingSelectionParams,
    SellValues,
} from '../config/types';
import { getModelPurchasePrice, getModelSellPrice } from '../requests/quote';

export enum QUOTE_DECLINED_REASONS {
    DECIDED_NOT_TO_SELL = 'DECIDED_NOT_TO_SELL',
    ADDED_IN_ERROR = 'ADDED_IN_ERROR',
    NOT_DECLINED = 'NOT_DECLINED',
}

/**
 * Check if the given sku is in the currently active quote
 *
 * @param {string} sku the product sku
 * @param {any} quoteData the quote data
 */
export function isInQuote(sku: string, quoteData: any): boolean {
    if (!quoteData) {
        return false;
    }

    // Check if current sku matches any products in the buy items
    return quoteData.buyValues.some(
        (item: any) => item.data?.product?.sku === sku
    );
}

/**
 * Return a quote item id, ensuring uniqueness in the current iteration
 */
export function getNextQuoteItemId(): string {
    return uuidv4();
}

export function composeQuoteExpiryAsDays(
    transaction: TransactionSummary
): number {
    if (!transaction?.quote?.expiryTime) return 0;

    return differenceInDays(
        parseISO(transaction?.quote?.expiryTime),
        new Date()
    );
}

export function getSavedAccessorySelection(
    priceModifiers?: readonly PriceModifier[]
): AccessorySelection | undefined {
    if (!priceModifiers?.length) {
        return undefined;
    }

    return {
        selectedModifierIds: priceModifiers
            .filter((modifier) => modifier.present)
            .map((modifier) => modifier.modelId),
    };
}

export async function getSellItemPrice({
    modelId,
    condition,
    market,
    accessoryPricingSelection,
}: GetSellItemPriceParams): Promise<SellValues> {
    if (accessoryPricingSelection.isAccessoriesChecklistEnabled) {
        return await getModelSellPrice(
            modelId,
            condition,
            accessoryPricingSelection.accessorySelection.selectedModifierIds,
            market
        );
    }

    const data = await getModelPurchasePrice(modelId, condition, market);

    return { value: data.purchaseValue, baseValue: data.purchaseValue };
}

export function resolveAccessoryPricingSelection({
    isAccessoriesChecklistEnabled,
    accessorySelection,
    defaultAccessorySelection,
}: ResolveAccessoryPricingSelectionParams):
    | AccessoryPricingSelection
    | undefined {
    if (!isAccessoriesChecklistEnabled) {
        return { isAccessoriesChecklistEnabled: false };
    }

    const resolvedAccessorySelection =
        accessorySelection ?? defaultAccessorySelection;

    return resolvedAccessorySelection
        ? {
              isAccessoriesChecklistEnabled: true,
              accessorySelection: resolvedAccessorySelection,
          }
        : undefined;
}
